import { createListenerMiddleware } from '@reduxjs/toolkit';
import { AuthenticationSliceShape, authenticationTokenSelector } from '@cp/shared/api/core';
import { DialogEventType } from '@common/model/dialog/dialogEventType';
import { DialogMessageEvent } from '@common/model/dialog/events/dialogMessage.event';
import { DialogMemberJoinedEvent } from '@common/model/dialog/events/dialogMemberJoined.event';
import { DialogMemberLeavedEvent } from '@common/model/dialog/events/dialogMemberLeaved.event';
import { DialogMemberOnlineEvent } from '@common/model/dialog/events/dialogMemberOnline.event';
import { DialogMemberOfflineEvent } from '@common/model/dialog/events/dialogMemberOffline.event';
import { DialogMessageDeliveredEvent } from '@common/model/dialog/events/dialogMessageDelivered.event';
import { DialogMessageReadEvent } from '@common/model/dialog/events/dialogMessageRead.event';
import { DialogMessageReceivedEvent } from '@common/model/dialog/events/dialogMessageReceivedEvent';
import { IoContainer } from '@cp/shared/di';
import { WEBSOCKET_MANAGER } from '@cp/shared/api/socket';
import { CoreDialogApiTags, coreDialogApi } from '../../api';
import { DialogReducerStateShape, dialogSlice } from './dialog';
import { dialogByIdSelector } from './selectors';

export const createDialogMiddleware = (extra: { container: IoContainer }) => {
  const dialogMiddleware = createListenerMiddleware<AuthenticationSliceShape>({ extra });
  dialogMiddleware.startListening({
    predicate: (_, currentState) => Boolean(authenticationTokenSelector(currentState as AuthenticationSliceShape)),
    effect: (_, { unsubscribe, dispatch, getState }) => {
      unsubscribe();
      const authToken = authenticationTokenSelector(getState()) as string;
      const manager = extra.container.get(WEBSOCKET_MANAGER);
      const socket = manager.socket('/dialog', { auth: { token: authToken } });
      socket.connect();
      socket.on(DialogEventType.NewMessage, (event: DialogMessageEvent) => {
        const { dialogId } = event.payload;
        // `messageAdded` can only patch a dialog the list already holds. A message from a brand new
        // interlocutor arrives in a dialog the list has never seen (the server hides dialogs without
        // a last message, so the `DialogCreated` refetch could not pick it up either) — the only way
        // to show it is to refetch the list.
        const isDialogInList = Boolean(dialogByIdSelector(getState() as unknown as DialogReducerStateShape, dialogId));

        dispatch(
          coreDialogApi.util.invalidateTags([
            CoreDialogApiTags.UnreadMessagesCount,
            { type: CoreDialogApiTags.Messages, id: dialogId },
            { type: CoreDialogApiTags.Dialog, id: dialogId },
            ...(isDialogInList ? [] : [CoreDialogApiTags.DialogList]),
          ]),
        );
        dispatch(dialogSlice.actions.messageAdded(event.payload));
        const deliveryReport = new DialogMessageReceivedEvent({
          dialogId,
          messageId: event.payload.message.id,
        });
        socket.emit(deliveryReport.type, deliveryReport);
      });
      socket.on(DialogEventType.MessageDelivered, (event: DialogMessageDeliveredEvent) => {
        dispatch(dialogSlice.actions.messageDelivered(event));
      });
      socket.on(DialogEventType.MessageRead, (event: DialogMessageReadEvent) => {
        dispatch(coreDialogApi.util.invalidateTags([CoreDialogApiTags.UnreadMessagesCount]));
        dispatch(dialogSlice.actions.messageRead(event));
      });
      socket.on(DialogEventType.MemberJoined, (event: DialogMemberJoinedEvent) => {
        dispatch(dialogSlice.actions.memberJoined(event));
      });
      socket.on(DialogEventType.MemberLeaved, (event: DialogMemberLeavedEvent) => {
        dispatch(dialogSlice.actions.memberLeaved(event));
      });
      socket.on(DialogEventType.MemberOnline, (event: DialogMemberOnlineEvent) => {
        dispatch(dialogSlice.actions.memberOnline(event));
      });
      socket.on(DialogEventType.MemberOffline, (event: DialogMemberOfflineEvent) => {
        dispatch(dialogSlice.actions.memberOffline(event));
      });
      socket.on(DialogEventType.DialogCreated, () => {
        dispatch(coreDialogApi.util.invalidateTags([{ type: CoreDialogApiTags.DialogList }]));
      });
    },
  });
  return dialogMiddleware.middleware;
};
